/**
 * Generic search utility that matches items based on searchable fields
 */

export interface SearchConfig<T> {
  /**
   * Fields to search in. Can be string field names or getter functions.
   */
  searchFields: Array<keyof T | ((item: T) => string | null | undefined)>
  /**
   * Case sensitive search. Defaults to false.
   */
  caseSensitive?: boolean
}

/**
 * Checks if an item matches the given search query based on configured search fields
 * @param item - The item to check
 * @param searchQuery - The search query string
 * @param config - Search configuration specifying which fields to search
 * @returns true if the item matches the query, false otherwise
 */
export function matchesSearchQuery<T>(item: T, searchQuery: string, config: SearchConfig<T>): boolean {
  if (!searchQuery.trim()) return true

  const query = config.caseSensitive ? searchQuery.trim() : searchQuery.toLowerCase().trim()

  return config.searchFields.some(field => {
    let value: string | null | undefined

    if (typeof field === 'function') {
      value = field(item)
    } else {
      value = item[field] as string | null | undefined
    }

    if (value == null) return false

    const searchValue = config.caseSensitive ? String(value) : String(value).toLowerCase()
    return searchValue.includes(query)
  })
}

/**
 * Pre-configured search function for common cases where items have name, description, and owner fields
 */
export function matchesCommonSearch<T>(item: T, searchQuery: string): boolean {
  return matchesSearchQuery(item, searchQuery, {
    searchFields: ['name', 'description', 'owner'] as Array<keyof T>,
    caseSensitive: false,
  })
}
